import React, { useState, useEffect } from 'react';
import { User } from '../../services/authService';
import { Task } from '../../services/taskService';
import TimeLogsService, { CreateTimeLogData } from '../../services/timelogsService';
import './TaskTimer.css';

interface TaskTimerProps {
  task: Task;
  user: User;
  onTimeLogCreated?: () => void;
  onClose?: () => void;
}

interface TimerState {
  isRunning: boolean;
  startTime: number | null;
  elapsedTime: number;
  description: string;
}

const TaskTimer: React.FC<TaskTimerProps> = ({ task, user, onTimeLogCreated, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [timer, setTimer] = useState<TimerState>({
    isRunning: false,
    startTime: null,
    elapsedTime: 0,
    description: ''
  });

  // Timer effect - update elapsed time every 100ms when running
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (timer.isRunning && timer.startTime) {
      interval = setInterval(() => {
        setTimer(prev => ({
          ...prev,
          elapsedTime: Date.now() - prev.startTime!
        }));
      }, 100);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer.isRunning, timer.startTime]);

  const formatTime = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const startTimer = () => {
    setTimer(prev => ({
      ...prev,
      isRunning: true,
      startTime: Date.now(),
      elapsedTime: 0
    }));
    setError(null);
  };

  const stopTimer = async () => {
    if (!timer.isRunning || !user) return;
    
    const durationMinutes = Math.floor(timer.elapsedTime / 60000);
    
    if (durationMinutes < 1) {
      setError('Timer must run for at least 1 minute');
      return;
    }
    
    try {
      setLoading(true);
      
      const endTime = new Date();
      const startTime = new Date(timer.startTime!);
      
      const timeLogData: CreateTimeLogData = {
        user_id: user.id,
        activity_type: 'task',
        activity_id: task.id.toString(),
        activity_name: task.title,
        duration_minutes: durationMinutes,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        description: timer.description || undefined,
        is_manual_entry: false
      };
      
      await TimeLogsService.createTimeLog(timeLogData);
      
      // Reset timer state
      setTimer({
        isRunning: false,
        startTime: null,
        elapsedTime: 0,
        description: ''
      });
      
      setError(null);
      
      // Call callback if provided
      if (onTimeLogCreated) {
        onTimeLogCreated();
      }
    } catch (err: any) {
      console.error('Error saving time log:', err);
      setError(err.message || 'Failed to save time log');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="task-timer-container">
      {error && (
        <div className="task-timer-error">
          {error}
        </div>
      )}

      <div className="task-timer-display-container">
        <div className="task-timer-display">
          <div className="task-timer-time">
            {formatTime(timer.elapsedTime)}
          </div>
          <div className="task-timer-status">
            {timer.isRunning ? 'Running' : 'Stopped'}
          </div>
        </div>
        
        <div className="task-timer-controls">
          <div className="task-timer-inputs">
            <input
              type="text"
              placeholder="Description (optional)"
              value={timer.description}
              onChange={(e) => setTimer(prev => ({ ...prev, description: e.target.value }))}
              disabled={timer.isRunning}
              className="task-timer-description-input"
            />
          </div>
          
          <div className="task-timer-buttons">
            {!timer.isRunning ? (
              <button 
                className="task-timer-start-btn"
                onClick={startTimer}
              >
                ▶ Start
              </button>
            ) : (
              <button 
                className="task-timer-stop-btn"
                onClick={stopTimer}
                disabled={loading}
              >
                ⏹ Stop
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskTimer;

